import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { ArrowLeft, ChevronDown, Swords, Users } from "lucide-react";
import { ChallengeRow } from "components/Cards/CardMisRetos.js";
import { getMyPrivateChallenges } from "utils/supabase";

const PAGE_SIZE = 10;

async function fetchChallengePage(page) {
  const { data, error } = await getMyPrivateChallenges(page, PAGE_SIZE);
  if (error) throw error;
  if (!data?.success) throw new Error("No se pudieron cargar los retos.");
  return data;
}

export default function Retos() {
  const history = useHistory();
  const [challenges, setChallenges] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let mounted = true;

    fetchChallengePage(1)
      .then((data) => {
        if (!mounted) return;
        setChallenges(Array.isArray(data.challenges) ? data.challenges : []);
        setPagination(data.pagination || null);
      })
      .catch((error) => {
        if (!mounted) return;
        console.error("Error fetching private challenges:", error);
        setErrorMessage("No se pudieron cargar tus retos. Intenta nuevamente.");
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const handleLoadMore = async () => {
    if (isLoadingMore || !pagination?.has_next_page) return;

    setIsLoadingMore(true);
    setErrorMessage("");
    try {
      const data = await fetchChallengePage(pagination.page + 1);
      setChallenges((current) => [...current, ...(Array.isArray(data.challenges) ? data.challenges : [])]);
      setPagination(data.pagination || null);
    } catch (error) {
      console.error("Error loading more private challenges:", error);
      setErrorMessage("No se pudieron cargar más retos. Intenta nuevamente.");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleNavigate = (route) => history.push(route);

  return (
    <div className="flex flex-wrap mt-100 justify-center">
      <div className="w-full lg:w-10/12 px-4">
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #f1f5f9",
            borderRadius: "12px",
            p: { xs: 1.5, sm: 2.5 },
            backgroundColor: "#ffffff",
            boxShadow: "0 18px 48px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2 }}>
            <Button
              startIcon={<ArrowLeft size={16} />}
              onClick={() => history.push("/admin/dashboard")}
              sx={{ textTransform: "none", color: "#64748b", fontWeight: 600, px: 0, minWidth: 0 }}
            >
              Dashboard
            </Button>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                flexShrink: 0,
                borderRadius: "12px",
                backgroundColor: "#ede9fe",
                color: "#7c3aed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Swords size={22} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: { xs: "1.15rem", sm: "1.35rem" }, fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
                Mis retos
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: "#64748b", mt: 0.25 }}>
                Retos que has creado o aceptado
              </Typography>
            </Box>
            {pagination?.total_items != null ? (
              <Typography sx={{ ml: "auto", fontSize: "0.8rem", color: "#64748b", whiteSpace: "nowrap" }}>
                {challenges.length} de {pagination.total_items}
              </Typography>
            ) : null}
          </Box>

          {errorMessage ? <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert> : null}

          {isLoading ? (
            <Stack spacing={0.6}>
              {Array.from({ length: 5 }).map((_, index) => (
                <Box key={`challenge-page-skeleton-${index}`} sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 1.1, py: 1.2 }}>
                  <Skeleton variant="circular" width={40} height={40} animation="wave" />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Skeleton variant="text" width="45%" height={22} animation="wave" />
                    <Skeleton variant="text" width="65%" height={17} animation="wave" />
                  </Box>
                  <Skeleton variant="rounded" width={160} height={34} animation="wave" />
                </Box>
              ))}
            </Stack>
          ) : challenges.length === 0 ? (
            <Box sx={{ minHeight: 220, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <Users size={28} color="#94a3b8" />
              <Typography sx={{ color: "#64748b", fontSize: "0.9rem" }}>
                Aún no has creado ni aceptado ningún reto.
              </Typography>
            </Box>
          ) : (
            <>
              <Stack spacing={0}>
                {challenges.map((challenge, index) => (
                  <ChallengeRow
                    key={`${challenge.challenge_id}-${challenge.exam_id || "challenge"}`}
                    challenge={challenge}
                    onNavigate={handleNavigate}
                    isLast={index === challenges.length - 1 && !pagination?.has_next_page}
                  />
                ))}
              </Stack>

              {pagination?.has_next_page ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    endIcon={isLoadingMore ? <CircularProgress size={15} /> : <ChevronDown size={16} />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "9px",
                      color: "#7c3aed",
                      borderColor: "#c4b5fd",
                      "&:hover": { borderColor: "#a78bfa", backgroundColor: "#f5f3ff" },
                    }}
                  >
                    {isLoadingMore ? "Cargando..." : "Cargar 10 más"}
                  </Button>
                </Box>
              ) : null}
            </>
          )}
        </Paper>
      </div>
    </div>
  );
}
